import { ReactNode, useState } from "react";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { clsx } from "clsx";
import { Link } from "react-router-dom";
import { questionRules } from "@/features/IcpFit/icp.ts";
import { auditPath, AuditProspect } from "./auditProspect.ts";
import { useAuditProspect } from "./AuditProspectContext.tsx";

/** How many values a row shows before the rest becomes "+3 more". */
const MAX_CHIPS = 6;

function Chips({
    values,
    tone = "fit",
}: {
    values: string[];
    tone?: "fit" | "never";
}) {
    const [expanded, setExpanded] = useState(false);
    const shown = expanded ? values : values.slice(0, MAX_CHIPS);
    const more = values.length - shown.length;

    return (
        <ul className="flex flex-wrap gap-1.5">
            {shown.map((value) => (
                <li
                    key={value}
                    className={clsx(
                        "rounded-full px-2.5 py-1 text-sm leading-5",
                        tone === "fit"
                            ? "bg-brand-purple/5 text-brand-purple"
                            : "bg-brand-pink/10 text-brand-purple",
                    )}
                >
                    {value}
                </li>
            ))}
            {(more > 0 || expanded) && (
                <li>
                    <button
                        type="button"
                        aria-expanded={expanded}
                        onClick={() => setExpanded(!expanded)}
                        className="rounded-full px-1 py-1 text-sm leading-5 text-brand-grey-warm underline-offset-2 hover:text-brand-purple hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink"
                    >
                        {expanded ? "Show fewer" : `+${more} more`}
                    </button>
                </li>
            )}
        </ul>
    );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
            <dt className="shrink-0 pt-1.5 font-brand-mono text-xs uppercase tracking-wide text-brand-grey-warm sm:w-28">
                {label}
            </dt>
            <dd className="min-w-0">{children}</dd>
        </div>
    );
}

/**
 * The company's own mark beside its domain, from the logo Cdn the app's
 * avatars use (catalyst/avatar.tsx). That one only asks the Cdn in a
 * production build; this page asks it everywhere, since the mark is the
 * point of the line. A company the Cdn has no logo for simply gets none.
 */
function DomainLogo({ domain }: { domain: string }) {
    const [missing, setMissing] = useState(false);
    if (missing) {
        return null;
    }

    return (
        <img
            src={`https://company-logos.spectaclehq.com/${encodeURIComponent(domain)}.png`}
            alt=""
            className="h-4 w-4 shrink-0 rounded-sm"
            onError={() => setMissing(true)}
        />
    );
}

/** A question without its "(e.g. …)" aside: the card is read at a glance, and the editor has the whole text. */
function briefly(question: string): string {
    return question.replace(/\s*\((?:e\.g\.|i\.e\.|such as)[^)]*\)/gi, "");
}

/**
 * The profile we drafted from the prospect's website: who fits, who never
 * does, and what we check per company, with the way into the editor.
 *
 * Read at a glance, so the server's lines are shown as chips and the
 * description the model wrote is left to the editor. The question texts are
 * the model's too, from a third party's website: rendered as text and
 * nowhere else.
 */
export function AuditProfileCard({
    token,
    prospect,
    editable = true,
}: {
    token: string;
    prospect: AuditProspect;
    /** Off for a viewer who is not the prospect: the profile is theirs to change. */
    editable?: boolean;
}) {
    const {
        industry: industries,
        companySize: sizes,
        country: countries,
        neverFit: never,
    } = prospect.profile;
    const questions = questionRules(prospect.rules);

    return (
        <div className="flex max-w-lg flex-col gap-6 rounded-2xl border border-brand-purple/10 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
                <h2 className="flex items-center gap-3 font-display text-xl">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-purple text-white">
                        <BuildingOffice2Icon className="h-5 w-5" />
                    </span>
                    Your ideal customer
                </h2>
                {editable && (
                    <Link
                        to={auditPath(token, true)}
                        className="rounded-full border border-brand-purple/20 px-3.5 py-1.5 text-sm font-medium hover:bg-brand-purple/5"
                    >
                        Edit
                    </Link>
                )}
            </div>

            <dl className="flex flex-col gap-4">
                {industries.length > 0 && (
                    <Row label="Industry">
                        <Chips values={industries} />
                    </Row>
                )}
                {sizes.length > 0 && (
                    <Row label="Size">
                        <Chips values={sizes} />
                    </Row>
                )}
                {countries.length > 0 && (
                    <Row label="Country">
                        <Chips values={countries} />
                    </Row>
                )}
                {never.length > 0 && (
                    <Row label="Never a fit">
                        <Chips values={never} tone="never" />
                    </Row>
                )}
            </dl>

            {questions.length > 0 && (
                <div className="flex flex-col gap-3 border-t border-brand-beige pt-5">
                    <span className="font-brand-mono text-xs uppercase tracking-wide text-brand-grey-warm">
                        Checked for every company
                    </span>
                    <ul className="flex flex-col gap-2.5 text-sm">
                        {questions.map((question) => (
                            <li key={question.id} className="flex gap-2.5">
                                {question.answer === "yes" ? (
                                    <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-purple" />
                                ) : (
                                    <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-pink" />
                                )}
                                <span>{briefly(question.text)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <p className="flex items-center gap-2 text-xs text-brand-grey-warm">
                <DomainLogo domain={prospect.domain} />
                <span>
                    Drafted from {prospect.domain}.
                    {editable && " You can change it at any time."}
                </span>
            </p>
        </div>
    );
}

/**
 * The right half of the audit landing screen, and of the code step after it.
 * Below `lg` the shell has no right half, and the landing screen shows the
 * card under its heading.
 */
export default function AuditProfilePanel() {
    const { token, openedToken, state } = useAuditProspect();
    if (openedToken === null || state.status !== "ready") {
        return <div className="hidden bg-brand-beige lg:block lg:w-1/2" />;
    }

    return (
        <div className="hidden flex-col justify-center gap-7 bg-brand-beige p-16 text-brand-purple lg:flex lg:w-1/2">
            <span className="font-brand-mono text-xs uppercase tracking-wide text-brand-pink">
                Drafted from your website
            </span>
            <AuditProfileCard
                token={openedToken}
                prospect={state.prospect}
                // Off the link itself the editor would cost them the code step.
                editable={token !== null}
            />
        </div>
    );
}
